/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';
import { FormattedMessage } from 'js/lib/coursera.react-intl';

import { Link, Typography, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { getLandingPageLink, logo } from 'bundles/coursera-plus-common/utils/courseraPlusUtils';
import CourseraPlusLogoSvg from 'bundles/page/components/CourseraPlusLogoSvg';
import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import DottedSeparator from 'bundles/unified-description-page-common/components/shared/DottedSeparator';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';

import _t from 'i18n!nls/xdp';

type PropsFromCaller = {
  isSubscribedToCourseraLite?: boolean;
  disableCTA?: boolean;
  size?: 'small';
};

type PropsToComponent = PropsFromCaller & { isSubscribedToCPlusOrLite?: boolean };

const styles = {
  container: css`
    display: flex;
    align-items: center;
  `,
  containerOverride: css`
    padding-left: var(--cds-spacing-50);
  `,
  courseraPlusLogoImage: css`
    position: relative;
    bottom: 1px;
  `,
  learnMoreLink: css`
    text-decoration-color: var(--cds-color-neutral-primary);
  `,
  typographyOverride: css`
    color: var(--cds-color-neutral-primary-weak);
  `,
};

// This is wrapper that just outputs a different typography component based on experiment value.
const TypographyWrapper = ({ children, size }: React.PropsWithChildren<{ size: PropsFromCaller['size'] }>) => {
  const { enableXDPRedesignPhase1, enableXDPRedesignHero } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 && enableXDPRedesignHero;

  if (enableXDPRedesign) {
    return (
      <Typography2 variant="bodySecondary" component="p" css={styles.typographyOverride}>
        {children}
      </Typography2>
    );
  }
  return (
    <Typography variant={size === 'small' ? 'body2' : 'body1'} component="p">
      {children}
    </Typography>
  );
};

export const HeroCourseraPlusOrLiteCta = ({
  isSubscribedToCPlusOrLite,
  isSubscribedToCourseraLite,
  disableCTA,
  size,
}: PropsToComponent) => {
  const eventingProductData = useEventingProductData();
  const track = useTracker();
  const { enableXDPRedesignPhase1, enableXDPRedesignHero } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignHero;

  const onClick = () => {
    track('click_button', {
      button: { name: 'description_page_c_plus_learn_more' },
      pageSection: {
        sectionName: 'hero_banner',
      },
      ...eventingProductData,
    });
  };

  return (
    <div css={styles.container}>
      <TypographyWrapper size={size}>
        <FormattedMessage
          message={_t('Included with {courseraPlusOrLite}')}
          courseraPlusOrLite={
            isSubscribedToCourseraLite ? (
              <Imgix
                src={logo.LITE_FULL_BLUE_HD}
                maxHeight={size === 'small' ? 10 : 12}
                alt={_t('Coursera Lite')}
                css={styles.courseraPlusLogoImage}
              />
            ) : (
              <CourseraPlusLogoSvg height={size === 'small' ? 10 : 12} />
            )
          }
        />
      </TypographyWrapper>
      {!isSubscribedToCPlusOrLite && !disableCTA && (
        <div css={[styles.container, enableXDPRedesign && styles.containerOverride]}>
          {!enableXDPRedesignHero && (
            <DottedSeparator
              color={enableXDPRedesign ? 'var(--cds-color-neutral-primary-weak)' : undefined}
              padding={enableXDPRedesign ? '--cds-spacing-50' : undefined}
            />
          )}
          <Link
            component={TrackedLink2}
            href={getLandingPageLink()}
            trackingName="coursera_plus_banner"
            css={styles.learnMoreLink}
            aria-label={_t('Learn more about Coursera Plus')}
            onClick={onClick}
          >
            <TypographyWrapper size={size}>{_t('Learn more')}</TypographyWrapper>
          </Link>
        </div>
      )}
    </div>
  );
};

export default HeroCourseraPlusOrLiteCta;
